import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { VgpsVideoView, useVgpsSessionState, useVgpsEvents, useQosState } from 'livekit_web/react';
import type { VgpsSession, AudioCapabilityState } from 'livekit_web';
import { useLiveSession } from '../hooks/use_live_session';
import { ErrorOverlay } from './ErrorOverlay';
import { QosIndicator } from './QosIndicator';

/* ── Types ───────────────────────────────────────────────────────── */
export type QualityMode = 'hd' | 'sd';

/**
 * Snapshot of the playback / media state exposed to consumers via
 * `onMediaStateChange` and `CameraTileHandle.getMediaState()`.
 *
 * Consumers should derive overlay UI (mute button, "LIVE" badge, screenshot
 * button) from this object rather than querying the SDK's internal DOM —
 * doing so insulates the embedder from future SDK changes (e.g. moving the
 * `<video>` element inside a shadow root).
 */
export interface CameraTileMediaState {
  /** True once frames are actively rendering (state in 'playing' or 'buffering'). */
  isLive: boolean;
  /** Current quality mode of the active stream. */
  quality: QualityMode;
  /** Pixel dimensions of the latest decoded frame (0 when unknown). */
  width: number;
  height: number;
  /**
   * Whether audio output is muted.
   *
   * Read back from the real `AudioContext` state, not from the last click:
   * if the browser refused the gesture the context stays suspended and this
   * stays `true`, so the button never lies about whether sound is coming.
   */
  muted: boolean;
  /** Audio volume in [0, 1]. */
  volume: number;
  /**
   * The device's audio capability for the CURRENT effective channel, as
   * advertised in `session_bootstrap`. Three states — see
   * `AudioCapabilityState`.
   *
   * Drive the speaker control from this, never from hardcoded channel numbers.
   * It changes under the user on an HD↔SD toggle, because 101/102/201/202 are
   * different channels with different answers.
   *
   * `'unknown'` means unproven, NOT negative: render an enabled button with no
   * "no microphone" affordance and let the wire decide. Never cache it as a no.
   */
  audioCapability: AudioCapabilityState;
  /**
   * Whether any audio has actually arrived on the wire for this stream.
   *
   * `audioCapability === 'available'` with this still false after ~5 s of video
   * is "no audio received" — a delivery problem worth surfacing — and is NOT
   * the same as "no microphone". Do not use a 2 s timeout: the device gates
   * audio uplink on viewer demand.
   */
  audioReceiving: boolean;
  /**
   * Why audio cannot play in this browser context at all, or null when it can.
   *
   * Distinct from both capability states: the device has a microphone and the
   * audio is arriving — this page just cannot play it. Almost always an
   * insecure origin, because `AudioWorklet` is secure-context only while
   * `AudioContext` still constructs and reports 'running', so there is no
   * symptom except silence. `http://<lan-ip>:3000` triggers it;
   * `http://localhost:3000` and any HTTPS origin do not.
   *
   * When this is set, `muted` stays true. Render it as the reason the speaker
   * control is disabled — never let the button show "unmuted" over silence.
   */
  audioUnavailableReason: string | null;
  /** Whether the tile is currently displayed fullscreen. */
  isFullscreen: boolean;
}

/**
 * Imperative API surface exposed via `ref` to consumers that need to drive
 * the tile from a parent component — e.g. attaching mute / screenshot /
 * fullscreen buttons to a tracking-view toolbar.
 *
 * Prefer this over querying the DOM (`tileEl.querySelector('video')`),
 * which depends on internal markup that is not part of the public contract.
 */
export interface CameraTileHandle {
  /** Returns the current media state (synchronous snapshot). */
  getMediaState(): CameraTileMediaState;
  /** Mute / unmute audio. Safe to call before media starts. */
  setMuted(muted: boolean): void;
  /** Set audio volume in [0, 1]. */
  setVolume(volume: number): void;
  /** Force the tile to a specific quality mode. */
  setQuality(quality: QualityMode): void;
  /**
   * Capture the current frame as a PNG `Blob`. Returns `null` if no frame
   * is currently rendering (e.g. tile is in `connecting` / `failed` state).
   */
  captureScreenshot(): Promise<Blob | null>;
  /**
   * Capture the current frame and download it as a PNG. Filename defaults
   * to `${deviceSerial}_${channelId}_${timestamp}.png`.
   */
  downloadScreenshot(filename?: string): Promise<void>;
  /** Enter fullscreen on this tile. Resolves once the request settles. */
  requestFullscreen(): Promise<void>;
  /** Exit fullscreen if this tile owns it. */
  exitFullscreen(): Promise<void>;
}

export interface CameraTileProps {
  deviceSerial: string;
  channelId: string;
  title: string;
  height?: string;
  initialQuality?: QualityMode;
  /**
   * Fires whenever the media state changes (live transition, mute toggle,
   * fullscreen toggle, resolution change). Use this in place of polling
   * `<video>` events to drive overlay UI.
   */
  onMediaStateChange?: (state: CameraTileMediaState) => void;
  /** Fires when the fullscreen state of this tile toggles. */
  onFullscreenChange?: (isFullscreen: boolean) => void;
  /**
   * Deprecated / no-op. The 'recovering' state no longer renders an overlay
   * (the adaptive slow-play buffer keeps the picture moving), so there is no
   * spinner to defer. Kept for API compatibility with callers that still pass
   * it; the value is ignored.
   */
  recoveringOverlayDelayMs?: number;
  /**
   * Release the relay WebSocket + keepalive while the tab is hidden, and
   * re-acquire on the visible transition.
   *
   * **Default `true`.** This doc said `false` while the implementation said
   * `true` (here and in `useLiveSession`), which matters more than a typo
   * usually would: it is the difference between a host passing
   * `pauseWhenHidden` to opt in and a host passing it to no effect at all.
   *
   * Holding the lease while hidden is not the safer-looking option it appears
   * to be. A backgrounded tab cannot drain frames under browser timer
   * throttling, and the relay's per-subscriber queue is 256 deep and SHARED
   * between video and audio, dropping on overflow — so a held lease
   * accumulates a backlog that costs this client both streams in the same
   * proportion when it comes back.
   *
   * Since audio landed there is a real tension here that this flag cannot
   * resolve on its own: a user listening to a vehicle's cabin while working in
   * another tab wants delivery to continue, and a user with a live grid open
   * does not. `true` is the right default for the grid; the listening case is
   * mitigated rather than solved — `CameraTile` re-applies the user's audible
   * choice when the session comes back, so audio resumes on its own instead of
   * silently returning muted.
   */
  pauseWhenHidden?: boolean;
}

const QUALITY_LABELS: Record<QualityMode, string> = { hd: 'HD', sd: 'SD' };
const QUALITY_ORDER: QualityMode[] = ['hd', 'sd'];

// ── Quality persistence ─────────────────────────────────────────────────────
function qualityStorageKey(deviceSerial: string, baseChannel: string): string {
  return `vgps_quality_${deviceSerial}_${baseChannel}`;
}
function loadSavedQuality(deviceSerial: string, baseChannel: string, fallback: QualityMode): QualityMode {
  try {
    const v = localStorage.getItem(qualityStorageKey(deviceSerial, baseChannel));
    if (v === 'hd' || v === 'sd') return v;
  } catch { /* ignore */ }
  return fallback;
}
function saveQuality(deviceSerial: string, baseChannel: string, q: QualityMode): void {
  try { localStorage.setItem(qualityStorageKey(deviceSerial, baseChannel), q); } catch { /* ignore */ }
}

/* ── Bitrate polling ─────────────────────────────────────────────── */
function useBitrateKbps(session: VgpsSession | null | undefined): number | null {
  const [bitrateKbps, setBitrateKbps] = useState<number | null>(null);
  const prevRef = useRef<{ bytes: number; time: number } | null>(null);

  useEffect(() => {
    if (!session) { setBitrateKbps(null); prevRef.current = null; return undefined; }
    let active = true;
    const poll = async (): Promise<void> => {
      if (!active) return;
      try {
        const snap = await session.debugSnapshot();
        if (!snap.message) return;
        const metrics = JSON.parse(snap.message) as { bytesReceived?: number };
        const bytes = metrics.bytesReceived;
        if (bytes === undefined) return;
        const now = Date.now();
        const prev = prevRef.current;
        if (prev !== null) {
          const deltaBytes = bytes - prev.bytes;
          const deltaSec = (now - prev.time) / 1000;
          if (deltaSec > 0 && deltaBytes >= 0 && active) {
            setBitrateKbps((deltaBytes * 8) / 1000 / deltaSec);
          }
        }
        prevRef.current = { bytes, time: now };
      } catch { /* ignore */ }
    };
    const id = setInterval(() => void poll(), 1000);
    return () => { active = false; clearInterval(id); prevRef.current = null; setBitrateKbps(null); };
  }, [session]);

  return bitrateKbps;
}

function formatBitrate(kbps: number): string {
  return `${kbps.toFixed(2).replace('.', ',')} k/s`;
}

/* ── Spinner ─────────────────────────────────────────────────────── */
function Spinner(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" style={{ animation: 'vgps-spin 0.9s linear infinite' }}>
      <style>{`@keyframes vgps-spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="16" cy="16" r="13" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
      <path d="M16 3 A13 13 0 0 1 29 16" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* ── Buffering progress ──────────────────────────────────────────── */
function useBufferingProgress(isBuffering: boolean): number {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isBuffering) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      setProgress(0);
      return undefined;
    }
    setProgress(0);
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 99) return 99;
        const step = p < 60 ? 3 : p < 85 ? 1.5 : 0.4;
        return Math.min(99, p + step);
      });
    }, 200);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isBuffering]);

  return Math.floor(progress);
}

/* ── QoS Stats Panel ────────────────────────────────────────────── */
interface QosSnapshot {
  sessionId: string;
  deviceSerial: string;
  channelId: string;
  state: string;
  transportFamily: string;
  codec: string;
  streamCodec?: string;
  decoderCodec?: string;
  rttMs?: number;
  jitterMs?: number;
  jitterBufferDelayMs?: number;
  framesPerSecond?: number;
  availableIncomingBitrateKbps?: number;
  bytesReceived?: number;
  packetLossRatio?: number;
  encoderQp?: number;
  actualFps?: number;
  appliedProfile?: string;
  timestampMs?: number;
  frameWidth?: number;
  frameHeight?: number;
  framesDropped?: number;
  decoderImplementation?: string;
  powerEfficientDecoder?: boolean;
}

function StatsRow({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'contents' }}>
      <span style={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', paddingRight: 10 }}>{label}</span>
      <span style={{ color: 'rgba(255,255,255,0.85)', wordBreak: 'break-all' }}>{value ?? '\u2014'}</span>
    </div>
  );
}

function StatsDivider(): JSX.Element {
  return <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.20)', margin: '0 10px', flexShrink: 0 }} />;
}

const QUALITY_COLORS: Record<string, string> = {
  good: '#4CAF50', degraded: '#FF9800', poor: '#F44336', unknown: 'rgba(255,255,255,0.4)',
};

function StatsCol({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{
      flex: '1 1 0',
      minWidth: 0,
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      columnGap: 0,
      rowGap: 0,
      alignContent: 'start',
    }}>
      {children}
    </div>
  );
}

function QosStatsPanel({
  session,
  quality,
  bitrateKbps,
  onClose,
}: {
  session: VgpsSession | null | undefined;
  quality: string;
  bitrateKbps: number | null;
  onClose: () => void;
}): JSX.Element {
  const [snap, setSnap] = useState<QosSnapshot | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // MediaCapabilities probe result: true=HW, false=SW, null=unknown
  const [hwCap, setHwCap] = useState<{ supported: boolean; powerEfficient: boolean } | null>(null);

  useEffect(() => {
    // Probe H264 decode capabilities via MediaCapabilities API (W3C, Chrome 66+, Firefox 63+, Safari 13+)
    // supported=true means codec works (SW or HW); powerEfficient=true means HW decoder active
    void (async () => {
      try {
        const result = await navigator.mediaCapabilities?.decodingInfo({
          type: 'file',
          video: {
            contentType: 'video/mp4; codecs="avc1.42E01E"',
            width: 1280, height: 720, bitrate: 2_000_000, framerate: 30,
          },
        });
        if (result) setHwCap({ supported: result.supported, powerEfficient: result.powerEfficient ?? false });
      } catch { /* browser doesn't support */ }
    })();
  }, []);

  useEffect(() => {
    if (!session) { setSnap(null); return undefined; }
    let active = true;
    const poll = async (): Promise<void> => {
      try {
        const s = await session.debugSnapshot();
        const m = s.message ? (JSON.parse(s.message) as Record<string, unknown>) : {};
        if (active) setSnap({
          sessionId: s.sessionId,
          deviceSerial: s.deviceSerial,
          channelId: s.channelId,
          state: s.state,
          transportFamily: s.transportFamily,
          codec: s.codec,
          ...(m as Partial<QosSnapshot>),
        });
      } catch { /* ignore */ }
    };
    void poll();
    const id = setInterval(() => void poll(), 1000);
    return () => { active = false; clearInterval(id); };
  }, [session]);

  // Tick every 500ms so elapsed time stays fresh
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const fmt = (v: number | undefined, unit: string, dec = 1): string =>
    v !== undefined ? `${v.toFixed(dec)} ${unit}` : '—';
  const fmtPct = (v: number | undefined): string =>
    v !== undefined ? `${(v * 100).toFixed(2)}%` : '—';
  const fmtElapsed = (ts: number | undefined): string => {
    if (!ts) return '—';
    const diffMs = now - ts;
    if (diffMs < 0) return '0m00.0s';
    const totalSec = diffMs / 1000;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}m${s.toFixed(1).padStart(4, '0')}s`;
  };

  const hwDecValue = (): string => {
    // Primary: WebRTC inbound-rtp powerEfficientDecoder (Chrome 118+)
    if (snap?.powerEfficientDecoder !== undefined) {
      const hw = snap.powerEfficientDecoder ? 'yes' : 'no';
      const impl = snap.decoderImplementation ?? '';
      return impl ? `${hw} (${impl})` : hw;
    }
    // Secondary: decoderImplementation string.
    // For stream_socket we report 'WebCodecs' (HW-accelerated path) or 'Wasm' (SW fallback).
    // For WebRTC, Chrome reports 'ExternalDecoder' (HW) or 'FFmpegVideo' (SW).
    if (snap?.decoderImplementation !== undefined && snap.decoderImplementation !== '') {
      const d = snap.decoderImplementation.toLowerCase();
      const hw = (
        d === 'webcodecs' ||
        d.includes('external') || d.includes('vaapi') || d.includes('d3d') ||
        d.includes('hw') || d.includes('videotoolbox')
      ) ? 'HW' : 'SW';
      return `${hw} (${snap.decoderImplementation})`;
    }
    // Fallback: MediaCapabilities probe — show supported + powerEfficient separately
    if (hwCap !== null) {
      const hw = hwCap.powerEfficient ? 'HW' : 'SW';
      return hwCap.supported ? hw : 'not supported';
    }
    return '—';
  };

  const qColor = QUALITY_COLORS[quality] ?? QUALITY_COLORS.unknown;
  const shortSession = snap?.sessionId ? snap.sessionId.slice(-8) : '—';
  const codecValue = snap?.streamCodec
    ? `${snap.streamCodec}${snap.decoderCodec ? ` · ${snap.decoderCodec}` : ''}`
    : (snap?.codec ?? '—');

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: 8, left: 8, width: '34%', minWidth: 300,
        backgroundColor: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)',
        borderRadius: 4,
        color: '#fff',
        fontFamily: 'Arial, sans-serif',
        fontSize: 11,
        lineHeight: 1.75,
        padding: '7px 26px 7px 10px',
        zIndex: 40,
        userSelect: 'text',
        cursor: 'default',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
      }}
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute', top: 4, right: 6,
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '2px 4px',
        }}
      >
        {'\u00d7'}
      </button>

      {/* Col 1: Session + Network */}
      <StatsCol>
        <StatsRow label="Session" value={shortSession} />
        <StatsRow label="Dev / Ch" value={snap ? `${snap.deviceSerial} / ${snap.channelId}` : '—'} />
        <StatsRow label="Transport" value={snap?.transportFamily ?? '—'} />
        <StatsRow label="Codec" value={codecValue} />
        <StatsRow
          label="Chất lượng"
          value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: qColor, display: 'inline-block', boxShadow: `0 0 5px ${qColor}` }} />
              {quality}
            </span>
          }
        />
        <StatsRow label="Rx bitrate" value={bitrateKbps !== null ? `${bitrateKbps.toFixed(0)} kbps` : '—'} />
        <StatsRow label="BW khả dụng" value={fmt(snap?.availableIncomingBitrateKbps, 'kbps', 0)} />
        <StatsRow label="RTT" value={fmt(snap?.rttMs, 'ms')} />
        <StatsRow label="Jitter" value={fmt(snap?.jitterMs, 'ms')} />
        <StatsRow label="Mất gói" value={fmtPct(snap?.packetLossRatio)} />
      </StatsCol>

      <StatsDivider />

      {/* Col 2: Video + Encoder */}
      <StatsCol>
        <StatsRow label="Độ phân giải" value={snap?.frameWidth !== undefined && snap?.frameHeight !== undefined ? `${snap.frameWidth}×${snap.frameHeight}` : '—'} />
        <StatsRow label="FPS (thu)" value={snap?.framesPerSecond !== undefined ? snap.framesPerSecond.toFixed(1) : '—'} />
        <StatsRow label="HW Decode" value={hwDecValue()} />
        <StatsRow label="Dropped" value={snap?.framesDropped !== undefined ? String(snap.framesDropped) : '—'} />
        <StatsRow label="FPS (camera)" value={snap?.actualFps !== undefined ? Number(snap.actualFps).toFixed(1) : '—'} />
        <StatsRow label="Encoder QP" value={snap?.encoderQp !== undefined ? String(snap.encoderQp) : '—'} />
        <StatsRow label="Applied" value={snap?.appliedProfile ?? '—'} />
        <StatsRow label="Cập nhật" value={fmtElapsed(snap?.timestampMs)} />
      </StatsCol>
    </div>
  );
}

/* ── CameraTile ──────────────────────────────────────────────────── */
export const CameraTile = forwardRef<CameraTileHandle, CameraTileProps>(function CameraTile({
  deviceSerial,
  channelId,
  title,
  height = '100%',
  initialQuality = 'sd',
  onMediaStateChange,
  onFullscreenChange,
  pauseWhenHidden = true,
}: CameraTileProps, ref): JSX.Element {
  // -- Per-tile quality -- persisted in localStorage per device+channel --
  const baseChannel = String(channelId).replace(/0[12]$/, '');
  const [qualityMode, setQualityMode] = useState<QualityMode>(
    () => loadSavedQuality(deviceSerial, baseChannel, initialQuality)
  );
  const [isSwitching, setIsSwitching] = useState(false);
  const [showQualityPicker, setShowQualityPicker] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const selectQuality = useCallback((q: QualityMode) => {
    setShowQualityPicker(false);
    if (q === qualityMode) return;
    saveQuality(deviceSerial, baseChannel, q);
    setQualityMode(q);
    setIsSwitching(true);
  }, [qualityMode, deviceSerial, baseChannel]);

  const effectiveChannelId = qualityMode === 'sd' ? `${baseChannel}02` : `${baseChannel}01`;

  // ── Reclaim flow ──────────────────────────────────────────────────
  // Bumping reopenKey forces useLiveSession to release the current lease and
  // request a fresh session_open. Used when this tab was evicted by another
  // viewer (single-viewer policy) and the user explicitly chooses to take the
  // camera back. After bumping, this tab becomes the new challenger and the
  // server will evict whichever tab currently holds the lock.
  const [reopenKey, setReopenKey] = useState(0);
  const { session, error } = useLiveSession({
    deviceSerial,
    channelId: effectiveChannelId,
    qualityHint: qualityMode,
    reopenKey,
    pauseWhenHidden,
  });
  const state = useVgpsSessionState(session);
  const quality = useQosState(session);

  // ── Overlay states ────────────────────────────────────────────────
  // Codec stall: session 'playing' but 0 frames decoded (e.g. H.265 on Chrome Linux)
  const [codecError, setCodecError] = useState<string | null>(null);
  // Session evicted: another viewer (or this user in another tab/browser) is
  // currently watching the camera. Persistent overlay until the user clicks
  // to reclaim or refreshes the page.
  const [evictedByOther, setEvictedByOther] = useState(false);
  // Session conflict (legacy challenger-prompt path; auto-grant Option C
  // never sets this, kept only for backward compatibility with older servers).
  type ConflictInfo = { sessionId: string; expiresAtMs: number; role: 'challenger' | 'incumbent' };
  const [conflictInfo, setConflictInfo] = useState<ConflictInfo | null>(null);
  // Long connecting: session in 'connecting' > 25 s → device offline / not registered
  const [longConnecting, setLongConnecting] = useState(false);
  // Server-driven offline: the signaling server emitted device_offline for this
  // session (never_connected at open, or disconnected mid-stream). Faster and more
  // authoritative than the 25 s longConnecting heuristic, which we keep as a
  // fallback for the connected-but-silent case the server can't observe.
  const [deviceOffline, setDeviceOffline] = useState<null | 'never_connected' | 'disconnected'>(null);
  const [sessionError, setSessionError] = useState<{ code: string; message: string } | null>(null);

  // Track tile size so overlays (e.g. codec error) can adapt their layout —
  // small tiles get a one-line summary, large tiles show the full message.
  // Use a callback ref so the ResizeObserver attaches whenever the main tile
  // div mounts (it lives behind a conditional render path — the connecting /
  // error placeholders use different roots).
  const [tileSize, setTileSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const tileElRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const tileRef = useCallback((el: HTMLDivElement | null) => {
    tileElRef.current = el;
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setTileSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);
  useEffect(() => () => { roRef.current?.disconnect(); }, []);
  const isCompactTile = tileSize.w > 0 && tileSize.w < 360;

  // ── Imperative API: media-element refs + state tracking ──────────────
  // Track the <video> / <canvas> elements rendered by VgpsVideoView so we
  // can expose mute / screenshot / fullscreen / media-state without forcing
  // consumers to query the DOM. The refs update synchronously via the
  // VgpsVideoView `onMediaElements` callback.
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);

  // Audio is NOT carried by the <video> element. Its srcObject is a canvas
  // captureStream — video tracks only — so `.muted` / `.volume` on it were
  // always a placebo. Real audio goes through the AudioContext owned by the
  // player adapter, reached via session.setAudioEnabled / setAudioVolume.
  // These two therefore start muted and are driven by 'audio_state' events.
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  /**
   * Whether the USER wants this tile audible, as distinct from whether it
   * currently is.
   *
   * `muted` is read back from the real AudioContext, which is right for
   * display and wrong for memory: an HD↔SD toggle changes `effectiveChannelId`,
   * which is in `useLiveSession`'s dependencies, so the session — and with it
   * the adapter and the AudioSink — is rebuilt. The new sink starts disabled,
   * publishes `enabled: false`, and the tile dutifully shows muted. The user
   * did not mute it; the tile changed underneath them. Same thing on every tab
   * hide/show, since `pauseWhenHidden` defaults to true.
   *
   * A ref rather than state: nothing renders from it, and it must not
   * re-trigger the effect that consumes it.
   */
  const audioWantedRef = useRef(false);
  const volumeRef = useRef(1);
  const [audioCapability, setAudioCapability] = useState<AudioCapabilityState>('unknown');
  const [audioReceiving, setAudioReceiving] = useState(false);
  const [audioUnavailableReason, setAudioUnavailableReason] = useState<string | null>(null);
  const [frameSize, setFrameSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleMediaElements = useCallback(
    (els: { videoElement: HTMLVideoElement | null; overlayCanvas: HTMLCanvasElement | null }) => {
      videoElRef.current = els.videoElement;
      canvasElRef.current = els.overlayCanvas;
    },
    [],
  );

  // Subscribe to <video> events for state-change notifications. The element
  // identity is stable for the lifetime of VgpsVideoView, so we re-bind only
  // when the session (and therefore the underlying surface) changes.
  useEffect(() => {
    const v = videoElRef.current;
    if (!v) return undefined;
    const sync = (): void => {
      // Deliberately does not touch muted/volume: this element has no audio
      // track and never will under the AudioContext design.
      if (v.videoWidth && v.videoHeight) {
        setFrameSize({ w: v.videoWidth, h: v.videoHeight });
      }
    };
    sync();
    const evs = ['loadedmetadata', 'resize', 'volumechange', 'play', 'pause', 'emptied'];
    evs.forEach((e) => v.addEventListener(e, sync));
    return () => { evs.forEach((e) => v.removeEventListener(e, sync)); };
  }, [session]);

  // Track fullscreen state on the tile element.
  useEffect(() => {
    const onFsChange = (): void => {
      const next = document.fullscreenElement === tileElRef.current;
      setIsFullscreen(next);
      onFullscreenChange?.(next);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [onFullscreenChange]);

  // Debug: log key state for SD→HD switching
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(`[CameraTile] ch=${effectiveChannelId} state=${state} isSwitching=${isSwitching} session=${session?.sessionId?.slice(-6) ?? 'null'}`);
  }, [state, isSwitching, session, effectiveChannelId]);

  // Track the session that was current when isSwitching was set to true.
  // We only clear the overlay once a *new* session (different sessionId) reaches
  // playing/buffering — not when the *old* session was already playing at click time.
  const switchingFromSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isSwitching) {
      switchingFromSessionIdRef.current = session?.sessionId ?? null;
    }
  }, [isSwitching, session]);

  useEffect(() => {
    if (!isSwitching) return;
    const isNewSession = session?.sessionId !== switchingFromSessionIdRef.current;
    // eslint-disable-next-line no-console
    console.log(`[CameraTile] switching check state=${state} isNewSession=${isNewSession} ch=${effectiveChannelId}`);
    if (isNewSession && (state === 'playing' || state === 'buffering' || state === 'failed' || state === 'stopped')) {
      // eslint-disable-next-line no-console
      console.log(`[CameraTile] clearing isSwitching — state=${state}`);
      setIsSwitching(false);
    }
  }, [state, isSwitching, session, effectiveChannelId]);

  // Reset per-session overlays when session identity changes
  useEffect(() => {
    setCodecError(null);
    setConflictInfo(null);
    setDeviceOffline(null);
    setSessionError(null);
    // Audio state belongs to the SINK, and the sink was just replaced. Carrying
    // it over means the speaker button describes a stream that no longer
    // exists: `audioReceiving: true` from the old channel suppresses the
    // "advertised audio, none delivered" affordance on the new one, and a
    // stale `unavailableReason` disables a control that would now work. The
    // fresh session re-announces all of this — capability on its bootstrap,
    // the rest on its first `audio_state` — so the honest value in between is
    // "not known yet", which for capability is 'unknown' and not 'unavailable'.
    setMuted(true);
    setAudioReceiving(false);
    setAudioCapability('unknown');
    setAudioUnavailableReason(null);
    // evictedByOther is intentionally NOT cleared here: a fresh session
    // starting up does not invalidate the eviction notice from the previous
    // session — only an explicit user action (reclaim click) or a successful
    // PLAY of this session should clear it.
  }, [session]);

  // Carry the user's audible choice across a session replacement.
  //
  // No fresh user gesture is needed, and none is available: the AudioContext
  // was created and resumed by the click that first unmuted this tile, it is
  // page-global, and `resume()` on an already-running context is a no-op. This
  // honours a choice the user already made rather than starting audio on their
  // behalf — `audioWantedRef` is set only by an explicit setMuted/setVolume.
  //
  // Once per session, not once per state change: if another tile takes audio
  // ownership afterwards (the hub silences this one), this must not fight it
  // back. The residual case is deliberate — switching quality on a tile the
  // user had unmuted re-claims audio for that tile, because that is the last
  // thing the user explicitly asked of it.
  const audioRestoredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session || !audioWantedRef.current) return;
    if (audioRestoredForRef.current === session.sessionId) return;
    if (state !== 'playing' && state !== 'buffering') return;
    audioRestoredForRef.current = session.sessionId;
    session.setAudioVolume(volumeRef.current);
    session.setAudioEnabled(true);
  }, [session, state]);

  // Once this tab successfully reaches a healthy playback state, the eviction
  // notice (if any) becomes obsolete: this tab IS now the active viewer.
  useEffect(() => {
    if (state === 'playing' && evictedByOther) setEvictedByOther(false);
  }, [state, evictedByOther]);

  // A device_offline notice is obsolete once media actually flows: a late-
  // connecting camera that recovers clears its own offline overlay.
  useEffect(() => {
    if (state === 'playing' && deviceOffline) setDeviceOffline(null);
  }, [state, deviceOffline]);

  useEffect(() => {
    if (state === 'playing' && sessionError) setSessionError(null);
  }, [state, sessionError]);

  // ── Codec stall detection ────────────────────────────────────────
  // After 6 s in 'playing', verify framesDecoded > 0 while bytes are arriving.
  // framesDecoded = 0 + bytesReceived > 0 means codec is unsupported by this browser.
  useEffect(() => {
    // Don't wipe codec error on transition into 'failed' — the SDK uses that
    // state to surface fail-fast capability messages (e.g. browser missing
    // HEVC over WebRTC). Only clear when the session re-enters a healthy
    // lifecycle state.
    if (state !== 'failed') setCodecError(null);
    if (state !== 'playing' || !session) return undefined;
    const timer = setTimeout(async () => {
      try {
        const snap = await session.debugSnapshot();
        if (!snap.message) return;
        const m = JSON.parse(snap.message) as { framesDecoded?: number; bytesReceived?: number; decoderImplementation?: string };
        const bytes = typeof m.bytesReceived === 'number' ? m.bytesReceived : 0;
        // If a decoder implementation is reported (e.g. 'Wasm', 'WebCodecs'), video IS being decoded
        // — framesDecoded may be 0 for non-WebRTC transports (jtt1078/stream_socket) even when playing fine.
        const hasWorkingDecoder = typeof m.decoderImplementation === 'string' && m.decoderImplementation !== '';
        if (!hasWorkingDecoder && bytes > 0 && m.framesDecoded === 0) {
          setCodecError(
            'Trình duyệt không hỗ trợ giải mã H.265.',
          );
        }
      } catch { /* ignore */ }
    }, 6000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ── Device offline / not registered ──────────────────────────────
  // Session stays in 'connecting' > 25 s when the device serial is unknown
  // to the signaling server or the device is offline.
  useEffect(() => {
    setLongConnecting(false);
    if (state !== 'connecting') return undefined;
    const timer = setTimeout(() => setLongConnecting(true), 25000);
    return () => clearTimeout(timer);
  }, [state]);

  // ── Session conflict overlay ──────────────────────────────────────
  // useVgpsEvents subscribes once at mount with a stale closure, so capture
  // the current session via a ref to avoid missing events that arrive before
  // the first render with a non-null session (e.g. an SDK fail-fast that
  // fires immediately after the offer is processed).
  const sessionRef = useRef<typeof session>(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Frame-quality 'recovering' shows NO overlay (no spinner, no text): the
  // adaptive slow-play jitter buffer keeps the picture moving through transient
  // loss, so a recovery indicator only reads as a false alarm. The session-level
  // 'buffering' state still shows its loading spinner (see overlay below).
  useVgpsEvents((event) => {
    const sess = sessionRef.current;
    if (!sess) return;
    // Audio capability for THIS session's effective channel. Re-emitted on
    // every bootstrap, so an HD↔SD toggle (101↔102 / 201↔202) naturally
    // re-reads it rather than carrying the old channel's answer forward.
    if (event.type === 'audio_capability' && event.sessionId === sess.sessionId) {
      setAudioCapability(event.capability);
    }
    if (event.type === 'audio_state' && event.sessionId === sess.sessionId) {
      // Read the button back from the real context state, not from the click:
      // if the browser refused the gesture, keep showing muted rather than lie.
      // A running AudioContext is NOT sufficient: on an insecure origin the
      // context runs happily while `audioWorklet` is undefined, so the graph
      // never gets built. Require the absence of a failure reason too, or the
      // button lights up over silence.
      const reason = event.unavailableReason ?? null;
      setAudioUnavailableReason(reason);
      setMuted(!(event.enabled && event.contextRunning && !reason));
      setAudioReceiving(event.receivingAudio);
    }
    if (event.type === 'session_conflict' && event.sessionId === sess.sessionId) {
      // Auto-grant policy (server Option C): the server only sends this to
      // the incumbent it just evicted. Show a persistent overlay so the user
      // knows the camera is being viewed elsewhere; the overlay will only
      // clear when the user explicitly reclaims (click) or the page reloads
      // (which naturally re-leases as a new challenger).
      if (event.role === 'incumbent') {
        setEvictedByOther(true);
      } else {
        // Legacy challenger-prompt path (older servers); use the short
        // informational toast.
        const expiresAtMs = event.expiresAtMs > 0 ? event.expiresAtMs : Date.now() + 4000;
        setConflictInfo({ sessionId: event.sessionId, expiresAtMs, role: event.role });
      }
    }
    if (event.type === 'quality_adapted' && event.sessionId === sess.sessionId) {
      // eslint-disable-next-line no-console
      console.log('[QoS:event] quality_adapted', event);
    }
    // Server-driven offline notice — show the offline overlay immediately instead
    // of waiting out the 25 s longConnecting heuristic. Cleared on state==='playing'.
    if (event.type === 'device_offline' && event.sessionId === sess.sessionId) {
      setDeviceOffline(event.reason);
    }
    if (event.type === 'session_error' && event.sessionId === sess.sessionId) {
      setSessionError({ code: event.code, message: event.message });
      if (event.code === 'device_not_connected') setDeviceOffline('disconnected');
    }
    // Surface fail-fast messages emitted by the SDK (e.g. browser-incompatible
    // codec on WebRTC) so the customer sees an actionable explanation instead
    // of a permanent black tile.
    if (
      event.type === 'player'
      && event.sessionId === sess.sessionId
      && event.state === 'failed'
      && typeof event.message === 'string'
      && event.message.length > 0
    ) {
      setCodecError(event.message);
    }
  });

  // Auto-dismiss conflict when server-side lock expires
  useEffect(() => {
    if (!conflictInfo) return undefined;
    const remaining = conflictInfo.expiresAtMs - Date.now();
    if (remaining <= 0) { setConflictInfo(null); return undefined; }
    const timer = setTimeout(() => setConflictInfo(null), remaining);
    return () => clearTimeout(timer);
  }, [conflictInfo]);

  const handleTakeover = useCallback(() => {
    session?.respondToTakeover(true);
    setConflictInfo(null);
  }, [session]);

  const handleDeclineTakeover = useCallback(() => {
    session?.respondToTakeover(false);
    setConflictInfo(null);
  }, [session]);

  // Reclaim the camera after this tab was evicted by another viewer. Bumping
  // reopenKey forces useLiveSession to release any stale lease and request a
  // fresh session_open — this tab becomes the new challenger and the server
  // will evict whichever tab currently holds the lock.
  const handleReclaim = useCallback(() => {
    setEvictedByOther(false);
    setReopenKey((k) => k + 1);
  }, []);

  // ── QoS console logging ───────────────────────────────────────────
  useEffect(() => {
    if (!session || quality === 'unknown') return;
    // eslint-disable-next-line no-console
    console.log(
      `[QoS] device=${deviceSerial} ch=${channelId} session=${session.sessionId} quality=${quality}`
    );
  }, [quality, deviceSerial, channelId, session]);

  const bitrateKbps = useBitrateKbps(session);
  const isLive = state === 'playing' || state === 'buffering';
  const isBuffering = state === 'buffering';
  const bufferingProgress = useBufferingProgress(isBuffering);

  // ── Public media-state snapshot ──────────────────────────────────
  // Single source of truth for both the imperative handle and the
  // onMediaStateChange callback. Rebuilt whenever any input changes —
  // the cost is negligible compared to the render itself.
  const mediaState: CameraTileMediaState = {
    isLive,
    quality: qualityMode,
    width: frameSize.w,
    height: frameSize.h,
    muted,
    volume,
    audioCapability,
    audioReceiving,
    audioUnavailableReason,
    isFullscreen,
  };

  // Notify the parent on any media-state change. Stringifying is the
  // simplest stable dependency; the snapshot is small (~6 fields).
  const mediaStateKey = JSON.stringify(mediaState);
  useEffect(() => {
    onMediaStateChange?.(mediaState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaStateKey, onMediaStateChange]);

  // ── Imperative handle (mute / screenshot / fullscreen / quality) ──
  // Exposed via React.forwardRef → consumers grab it with useRef<CameraTileHandle>.
  // Each method is intentionally pure: no side effects beyond the action it
  // describes, so it is safe to call from event handlers (button clicks).
  useImperativeHandle(ref, (): CameraTileHandle => ({
    getMediaState: () => mediaState,
    // Signature unchanged — this is published API (0.2.0's dist/index.d.ts) and
    // three CMS toolbars already call it. Only the destination moved: from the
    // <video> element's placebo `.muted` to the adapter's real audio node.
    //
    // This call site is a genuine user gesture, which is exactly why the
    // AudioContext resume lives at the end of this chain: session →
    // StreamSocketTransport → HybridPlayerAdapter → AudioSink, with
    // `ctx.resume()` reached synchronously before anything is awaited.
    setMuted: (m: boolean) => {
      if (!session) return;
      // Unmuting from silence should actually be audible without the user
      // hunting for a second control.
      if (!m && volume === 0) {
        session.setAudioVolume(0.5);
        setVolume(0.5);
        volumeRef.current = 0.5;
      }
      // The user's explicit choice, remembered across session replacements.
      audioWantedRef.current = !m;
      audioRestoredForRef.current = session.sessionId;
      session.setAudioEnabled(!m);
      // Optimistic only for the mute direction, which cannot fail. The unmute
      // direction waits for the 'audio_state' event to confirm the context is
      // really running.
      if (m) setMuted(true);
    },
    setVolume: (vol: number) => {
      if (!session) return;
      const clamped = Math.max(0, Math.min(1, vol));
      session.setAudioVolume(clamped);
      setVolume(clamped);
      // Volume lives on the tile but is applied to the sink, and the sink is
      // replaced on every quality switch. Without this the new sink runs at its
      // own default while getMediaState() still reports the old number.
      volumeRef.current = clamped;
      if (clamped > 0 && muted) { audioWantedRef.current = true; session.setAudioEnabled(true); }
      if (clamped === 0) { audioWantedRef.current = false; session.setAudioEnabled(false); setMuted(true); }
      audioRestoredForRef.current = session.sessionId;
    },
    setQuality: (q: QualityMode) => {
      if (q === qualityMode) return;
      saveQuality(deviceSerial, baseChannel, q);
      setQualityMode(q);
      setIsSwitching(true);
    },
    captureScreenshot: async (): Promise<Blob | null> => {
      const source: HTMLVideoElement | HTMLCanvasElement | null =
        videoElRef.current ?? canvasElRef.current;
      if (!source) return null;
      const w = (source as HTMLVideoElement).videoWidth || (source as HTMLCanvasElement).width || source.clientWidth;
      const h = (source as HTMLVideoElement).videoHeight || (source as HTMLCanvasElement).height || source.clientHeight;
      if (!w || !h) return null;
      const off = document.createElement('canvas');
      off.width = w; off.height = h;
      const ctx = off.getContext('2d');
      if (!ctx) return null;
      try { ctx.drawImage(source, 0, 0, w, h); } catch { return null; }
      return await new Promise<Blob | null>((resolve) => off.toBlob((b) => resolve(b), 'image/png'));
    },
    downloadScreenshot: async (filename?: string): Promise<void> => {
      const source: HTMLVideoElement | HTMLCanvasElement | null =
        videoElRef.current ?? canvasElRef.current;
      if (!source) return;
      const w = (source as HTMLVideoElement).videoWidth || (source as HTMLCanvasElement).width || source.clientWidth;
      const h = (source as HTMLVideoElement).videoHeight || (source as HTMLCanvasElement).height || source.clientHeight;
      if (!w || !h) return;
      const off = document.createElement('canvas');
      off.width = w; off.height = h;
      const ctx = off.getContext('2d');
      if (!ctx) return;
      try { ctx.drawImage(source, 0, 0, w, h); } catch { return; }
      const a = document.createElement('a');
      a.href = off.toDataURL('image/png');
      a.download = filename ?? `${deviceSerial}_ch${channelId}_${Date.now()}.png`;
      a.click();
    },
    requestFullscreen: async (): Promise<void> => {
      const el = tileElRef.current;
      if (!el || document.fullscreenElement === el) return;
      await el.requestFullscreen?.();
    },
    exitFullscreen: async (): Promise<void> => {
      if (document.fullscreenElement !== tileElRef.current) return;
      await document.exitFullscreen?.();
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
    // `session` is a dependency because setMuted/setVolume now route audio
    // through it; a stale closure here would send the unmute to a session the
    // tile has already replaced (e.g. right after an HD↔SD switch).
    // `mediaStateKey` already covers `muted` and `volume`.
  }), [mediaStateKey, qualityMode, deviceSerial, baseChannel, channelId, session, muted, volume]);

  // ── Overlay auto-hide (5s, click to toggle) ──────────────────────
  const [overlayVisible, setOverlayVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startHideTimer = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setOverlayVisible(false), 5000);
  }, []);

  useEffect(() => {
    startHideTimer();
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [startHideTimer]);

  const handleTileClick = useCallback(() => {
    if (showQualityPicker) { setShowQualityPicker(false); return; }
    setOverlayVisible((prev) => {
      const next = !prev;
      if (next) startHideTimer();
      else if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
      return next;
    });
  }, [startHideTimer, showQualityPicker]);

  // ── Shared pill style ─────────────────────────────────────────────
  const pill: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 999,
    backgroundColor: 'rgba(8,19,32,0.72)',
    color: '#fff', fontSize: 11,
    backdropFilter: 'blur(4px)',
  };

  // ── Render: error ─────────────────────────────────────────────────
  if (error) return <ErrorOverlay error={error} height={height} />;

  // ── Render: no session (connecting) ──────────────────────────────
  if (!session && !isSwitching) {
    return (
      <div
        role="button" tabIndex={0}
        onClick={handleTileClick}
        onKeyDown={(e) => e.key === 'Enter' && handleTileClick()}
        style={{ position: 'relative', width: '100%', height, backgroundColor: '#101820', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer' }}
      >
        <div style={{ ...pill, position: 'absolute', bottom: 8, left: 8, backgroundColor: 'rgba(8,19,32,0.55)', fontWeight: 700, fontSize: 12 }}>{title}</div>
        Đang kết nối…
      </div>
    );
  }

  return (
    <div
      ref={tileRef}
      role="button" tabIndex={0}
      onClick={handleTileClick}
      onKeyDown={(e) => e.key === 'Enter' && handleTileClick()}
      style={{ position: 'relative', width: '100%', height, backgroundColor: '#101820', overflow: 'hidden', cursor: 'pointer' }}
    >
      {/* Video */}
      {session && (
        <VgpsVideoView
          session={session}
          muted
          playsInline
          objectFit={isFullscreen ? 'contain' : 'cover'}
          style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
          onMediaElements={handleMediaElements}
        />
      )}

      {/* ── Quality-switching overlay ── */}
      {isSwitching && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
          backgroundColor: 'rgba(8,19,32,0.82)', zIndex: 20,
          pointerEvents: 'none',
        }}>
          <Spinner />
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>Đang chuyển độ phân giải…</span>
        </div>
      )}

      {/* ── Device offline / not registered overlay ── */}
      {/* Server-driven device_offline shows immediately (any non-playing state);
          the 25 s longConnecting heuristic remains a fallback while 'connecting'. */}
      {(deviceOffline || sessionError || (longConnecting && state === 'connecting')) && state !== 'playing' && state !== 'stopped' && !isSwitching && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
          backgroundColor: 'rgba(8,19,32,0.92)', pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 28, opacity: 0.5 }}>⦸</span>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600 }}>
            {sessionError?.code === 'device_not_connected'
              ? 'Camera đang kết nối'
              : deviceOffline === 'disconnected' ? 'Camera mất kết nối' : 'Camera offline'}
           </span>
           {sessionError && sessionError.code !== 'device_not_connected' && (
             <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
               {sessionError.code}: {sessionError.message}
             </span>
           )}
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>{deviceSerial}</span>
        </div>
      )}

      {/* ── Codec not supported error overlay ── */}
      {codecError && !isSwitching && (() => {
        // First sentence (up to first ". ") is the actionable summary; remaining
        // sentences are the contact-support hint. Splitting on ". " (period +
        // space) avoids cutting tokens like "H.265" mid-string. Compact tiles
        // show only the summary with an ellipsis (full text on hover).
        const sepIdx = codecError.indexOf('. ');
        const firstSentence = sepIdx >= 0 ? codecError.slice(0, sepIdx + 1) : codecError;
        const showFull = !isCompactTile;
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', inset: 0, zIndex: 21,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: isCompactTile ? 4 : 10,
              padding: isCompactTile ? '0 12px' : '0 24px',
              backgroundColor: 'rgba(8,19,32,0.92)', textAlign: 'center',
            }}
          >
            <span style={{ color: '#e05566', fontSize: isCompactTile ? 22 : 28 }}>&#x2715;</span>
            <span style={{
              color: '#e05566',
              fontSize: isCompactTile ? 12 : 14,
              fontWeight: 600,
            }}>Lỗi giải mã video</span>
            <span
              title={codecError}
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: isCompactTile ? 11 : 12,
                lineHeight: 1.55,
                maxWidth: showFull ? 460 : '100%',
              }}
            >
              {showFull ? codecError : `${firstSentence} …`}
            </span>
            {qualityMode === 'hd' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); selectQuality('sd'); }}
                style={{
                  marginTop: isCompactTile ? 4 : 8,
                  padding: isCompactTile ? '3px 10px' : '5px 16px',
                  border: 'none', borderRadius: 999, cursor: 'pointer',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: '#fff', fontSize: isCompactTile ? 11 : 12, fontWeight: 600,
                  pointerEvents: 'auto',
                }}
              >
                Chuyển về SD
              </button>
            )}
          </div>
        );
      })()}

      {/* ── Eviction overlay — persistent until user clicks reclaim ── */}
      {/* Single-viewer policy: another tab/browser is currently watching this
          camera. Click anywhere on the overlay to reclaim — this tab will
          re-lease the session and the other viewer will receive its own
          eviction overlay. */}
      {evictedByOther && (() => {
        const summary = 'Đang mở camera trên trình duyệt hoặc tab khác.';
        const detail = 'Bấm vào đây để mở lại trên trình duyệt này (viewer hiện tại sẽ bị ngắt).';
        const showFull = !isCompactTile;
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); handleReclaim(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleReclaim(); } }}
            title={`${summary} ${detail}`}
            style={{
              position: 'absolute', inset: 0, zIndex: 26,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: isCompactTile ? 6 : 12,
              padding: isCompactTile ? '0 14px' : '0 28px',
              backgroundColor: 'rgba(8,19,32,0.93)', textAlign: 'center',
              backdropFilter: 'blur(4px)',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: isCompactTile ? 22 : 32 }}>📺</span>
            <span style={{
              color: '#fff', fontWeight: 600,
              fontSize: isCompactTile ? 12 : 14, lineHeight: 1.5,
              maxWidth: showFull ? 420 : '100%',
            }}>{summary}</span>
            {showFull && (
              <span style={{
                color: 'rgba(255,255,255,0.65)', fontSize: 12, lineHeight: 1.55,
                maxWidth: 420,
              }}>{detail}</span>
            )}
          </div>
        );
      })()}

      {/* ── Session conflict overlay (informational — auto-grant, no user action needed) ── */}
      {conflictInfo && !evictedByOther && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 25,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
          backgroundColor: 'rgba(8,19,32,0.93)', padding: '0 20px', textAlign: 'center',
          backdropFilter: 'blur(4px)',
          pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 28 }}>⚠️</span>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1.6 }}>
            {conflictInfo.role === 'incumbent'
              ? 'Phiên của bạn đã bị tiếp quản bởi viewer khác.'
              : 'Đang kết nối…'}
          </span>
        </div>
      )}

      {/* ── Buffering spinner (bottom-center) — initial load only ── */}
      {isBuffering && !isSwitching && (
        <div style={{
          position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          pointerEvents: 'none', zIndex: 10,
        }}>
          <Spinner />
          <span style={{ ...pill, whiteSpace: 'nowrap', fontSize: 11 }}>
            {`Vui lòng đợi trong khi tải…${bufferingProgress}%`}
          </span>
        </div>
      )}

      {/* ── TOP-LEFT: live indicator + bitrate (auto-hides) ── */}
      <div style={{
        position: 'absolute', top: 8, left: 8,
        display: 'flex', alignItems: 'center', gap: 6,
        pointerEvents: 'none', zIndex: 5,
        opacity: overlayVisible ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}>
        {isLive && (
          <div style={pill}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#4CAF50', display: 'inline-block', flexShrink: 0 }} />
            Trực tiếp
          </div>
        )}
        {bitrateKbps !== null && (
          <div style={pill}>{formatBitrate(bitrateKbps)}</div>
        )}
      </div>

      {/* ── TOP-RIGHT: QoS indicator + quality picker (always visible) ── */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 15, display: 'flex', alignItems: 'center', gap: 5 }}>

        {/* QoS indicator button — same pill size as HD button, click to toggle stats panel */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowStats((v) => !v); }}
          title="Stats QoS"
          style={{
            ...pill,
            border: 'none',
            cursor: 'pointer',
            fontWeight: 700,
            backgroundColor: showStats ? 'rgba(255,255,255,0.18)' : 'rgba(8,19,32,0.72)',
          } as React.CSSProperties}
        >
          <QosIndicator session={session} compact style={{ background: 'none', backdropFilter: 'none', padding: 0, fontSize: 11 }} />
        </button>

        {/* Quality picker */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowQualityPicker((v) => !v); }}
            style={{ ...pill, border: 'none', cursor: 'pointer', fontWeight: 700 } as React.CSSProperties}
          >
            {QUALITY_LABELS[qualityMode]}
            <span style={{ fontSize: 8, marginLeft: 2, opacity: 0.7 }}>▾</span>
          </button>

          {showQualityPicker && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                backgroundColor: 'rgba(8,19,32,0.92)', borderRadius: 8,
                padding: '4px 0', minWidth: 70,
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)', zIndex: 30,
              }}
            >
              {QUALITY_ORDER.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => selectQuality(q)}
                  style={{
                    display: 'block', width: '100%', padding: '6px 14px',
                    textAlign: 'left', border: 'none', cursor: 'pointer',
                    backgroundColor: q === qualityMode ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: q === qualityMode ? '#fff' : 'rgba(255,255,255,0.65)',
                    fontSize: 12, fontWeight: q === qualityMode ? 700 : 400,
                  }}
                >
                  {QUALITY_LABELS[q]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── BOTTOM-LEFT: channel title (always visible) ── */}
      <div style={{
        ...pill,
        position: 'absolute', bottom: 8, left: 8,
        backgroundColor: 'rgba(8,19,32,0.55)',
        fontWeight: 700, fontSize: 12,
        pointerEvents: 'none', zIndex: 5,
      }}>
        {title}
      </div>

      {/* ── QoS Stats Panel (toggled by QoS indicator click) ── */}
      {showStats && (
        <QosStatsPanel
          session={session}
          quality={quality}
          bitrateKbps={bitrateKbps}
          onClose={() => setShowStats(false)}
        />
      )}
    </div>
  );
});
